import { z } from "zod";
import { previewUrlSchema } from "./preview-url.js";
const easingSchema = z.enum(["linear", "easeIn", "easeOut", "easeInOut"]);
const linearTimingSchema = z.object({
    variant: z.literal("linear"),
    durationSeconds: z.number(),
    easing: easingSchema.optional(),
});
const springTimingSchema = z.object({
    variant: z.literal("spring"),
    durationSeconds: z.number().optional(),
    durationRestThreshold: z.number().optional(),
    config: z
        .object({
        damping: z.number().optional(),
        mass: z.number().optional(),
        stiffness: z.number().optional(),
        overshootClamping: z.boolean().optional(),
    })
        .optional(),
});
const bezierTimingSchema = z.object({
    variant: z.literal("bezier"),
    durationSeconds: z.number(),
    config: z
        .object({
        p0: z.number(),
        p1: z.number(),
        p2: z.number(),
        p3: z.number(),
    })
        .optional(),
});
const timingSchema = z.discriminatedUnion("variant", [
    linearTimingSchema,
    springTimingSchema,
    bezierTimingSchema,
]);
const transitionCommonSchema = z.object({
    timing: timingSchema,
    customProps: z.record(z.string(), z.any()).optional(),
});
const fadeOutTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("fadeOut"),
});
const fadeTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("fade"),
    // What percentage (0-1) of the transition should the entering and exiting
    // part of the transition take up.
    splitProportion: z.number().min(0).max(1).optional(),
});
const slideTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("slide"),
    direction: z.enum(["from-left", "from-right", "from-top", "from-bottom"]),
});
const slideFadeTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("slide-fade"),
    direction: z.enum(["from-left", "from-right", "from-top", "from-bottom"]),
});
const slideInOutTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("slide-in-out"),
    direction: z.enum(["from-left", "from-right", "from-top", "from-bottom"]),
});
const marginSlideTransitionSchema = transitionCommonSchema.extend({
    // margin slide works just like slide, but as it doesn't use a
    // transform, it allows 'fixed' position children to be relative to the viewport
    variant: z.literal("marginSlide"),
    direction: z.enum(["from-left", "from-right", "from-top", "from-bottom"]),
});
const wipeTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("wipe"),
    direction: z.enum([
        "from-left",
        "from-right",
        "from-top",
        "from-bottom",
        "from-top-left",
        "from-top-right",
        "from-bottom-left",
        "from-bottom-right",
    ]),
});
const flipTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("flip"),
    direction: z.enum(["from-left", "from-right", "from-top", "from-bottom"]),
});
const scaleTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("scale"),
});
const zoomInTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("zoom-in"),
});
const imageSequenceSchema = z.object({
    urlPrefix: previewUrlSchema,
    frameCount: z.number(),
    zeroPad: z.number().optional(),
});
const imageSequenceTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("imageSequence"),
    overlay: imageSequenceSchema,
    mask: imageSequenceSchema,
});
const fadeOutScaleInTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("fadeOutScaleIn"),
});
const diamondMaskTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("diamondMask"),
});
const roundedRectRevealTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("roundedRectReveal"),
});
const centerWipeTransitionSchema = transitionCommonSchema.extend({
    variant: z.literal("centerWipe"),
    direction: z.enum(["vertical", "horizontal"]),
    disableExitFade: z.boolean().optional(),
});
const noneTransitionSchema = z.object({
    variant: z.literal("none"),
});
export const transitionSchema = z.discriminatedUnion("variant", [
    fadeOutTransitionSchema,
    fadeTransitionSchema,
    slideTransitionSchema,
    slideFadeTransitionSchema,
    marginSlideTransitionSchema,
    wipeTransitionSchema,
    flipTransitionSchema,
    scaleTransitionSchema,
    zoomInTransitionSchema,
    imageSequenceTransitionSchema,
    fadeOutScaleInTransitionSchema,
    noneTransitionSchema,
    diamondMaskTransitionSchema,
    roundedRectRevealTransitionSchema,
    centerWipeTransitionSchema,
    slideInOutTransitionSchema,
]);
